import { IBulkCartUpdatePayload, IExotelContextSignal } from "@/api_framework/api_modals/Exotel";
import { RouteKeys } from "@/api_framework/api_modals/Routers";
import { useBulkCartUpdateAPI } from "@/app/hooks/api_hooks/ameyo/useBulkCartUpdateAPI";
import { useExotelContextEngineAPI } from "@/app/hooks/api_hooks/ameyo/useExotelContextEngineAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { RESET_SLOTS_SLICE, setSlotBookingFilters } from "@/app_redux/reducers/slices/auth/slots_slice";
import { setCustomerAndPetId } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { DIAGNOSTICS_SERVICE_TYPE_ID, toDiagnosticsReplacement } from "@/app/utils/bookingServiceSelection";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const THEME_STYLES = {
  red: {
    container: "bg-[#FDF3F2] border-[#F7DDDA]",
    label: "text-[#C0392B]",
    solidButton: "bg-[#C0392B] hover:bg-[#A93226] text-white",
    selectedButton: "bg-[#FDF3F2] border-[#C0392B]",
  },
  yellow: {
    container: "bg-[#FFF9E8] border-[#F6E4A6]",
    label: "text-[#B7791F]",
    solidButton: "bg-[#D69E2E] hover:bg-[#B7791F] text-white",
    selectedButton: "bg-[#FFF9E8] border-[#D69E2E]",
  },
  green: {
    container: "bg-[#F0FAF4] border-[#D6EEDF]",
    label: "text-[#1E7C46]",
    solidButton: "bg-[#1E7C46] hover:bg-[#196B3C] text-white",
    selectedButton: "bg-[#F0FAF4] border-[#1E7C46]",
  },
  gray: {
    container: "bg-[#F7F7F6] border-[#E8E8E6]",
    label: "text-[#6B7280]",
    solidButton: "bg-[#374151] hover:bg-[#1F2937] text-white",
    selectedButton: "bg-[#F7F7F6] border-[#6B7280]",
  },
  orange: {
    container: "bg-[#FFF8EE] border-[#F6E6CB]",
    label: "text-[#B7791F]",
    solidButton: "bg-[#B7791F] hover:bg-[#9C6518] text-white",
    selectedButton: "bg-[#FFF8EE] border-[#B7791F]",
  },
  purple: {
    container: "bg-[#F5F3FF] border-[#E4DCFB]",
    label: "text-[#6D28D9]",
    solidButton: "bg-[#6D28D9] hover:bg-[#5B21B6] text-white",
    selectedButton: "bg-[#F5F3FF] border-[#6D28D9]",
  },
  blue: {
    container: "bg-[#F1F4FD] border-[#DCE3F7]",
    label: "text-[#2D4EA8]",
    solidButton: "bg-[#2A3F9D] hover:bg-[#22337F] text-white",
    selectedButton: "bg-[#F1F4FD] border-[#2A3F9D]",
  },
} as const;

type ThemeColor = keyof typeof THEME_STYLES;

const ICON_MAP: Record<string, string> = {
  ticket_icon: "🎫",
  lab_icon: "🧪",
  reminder_icon: "🔔",
  vaccination_icon: "💉",
  appointment_icon: "📅",
  order_icon: "📦",
  qcomm_order_icon: "📦",
  call_icon: "📞",
  past_calls_icon: "📞",
  membership_icon: "⭐",
  form_icon: "📝",
  incomplete_form_icon: "📝",
};

const CATEGORY_ROUTE_MAP: Partial<Record<string, RouteKeys>> = {
    "Reminders": "VACCINATION_TAB",
    "Appointments": "APPOINTMENTS_TAB",
    "SD Tickets": "TICKECT_TAB",
    "Lab Report": "LAB_TEST_TAB",
    "Vaccination": "VACCINATION_TAB",
    "Membership": "MEMBERSHIP_DETAILS_TAB",
    "Membership Status": "MEMBERSHIP_DETAILS_TAB",
    "Qcomm Orders": "ORDER_MANAGEMENT",
    "Orders": "ORDER_MANAGEMENT",
};

const ACTION_ROUTE_MAP: Partial<Record<string, RouteKeys>> = {
    "Book Appointment": "APPOINTMENTS_TAB",
    "Book Again": "APPOINTMENTS_TAB",
    "Update Records": "VACCINATION_TAB",
    "Renew": "MEMBERSHIP_DETAILS_TAB",
    "Order Again": "ORDER_MANAGEMENT",
};

const CUSTOMER_PROFILE_TAB_ROUTES = new Set<RouteKeys>([
    "REMINDERS_TAB",
    "APPOINTMENTS_TAB",
    "TICKECT_TAB",
    "LAB_TEST_TAB",
    "VACCINATION_TAB",
    "MEMBERSHIP_DETAILS_TAB",
]);

const getThemeStyle = (color: string | undefined) => {
    const theme = (color ?? "gray").toLowerCase() as ThemeColor;
    return THEME_STYLES[theme] ?? THEME_STYLES["gray"];
};

const resolveTalkingPointIcon = (signal: IExotelContextSignal): string =>
    ICON_MAP[signal?.icon ?? ""] ?? "📌";

const categoryRouteForSignal = (signal: IExotelContextSignal): RouteKeys | null =>
    CATEGORY_ROUTE_MAP[signal?.category ?? ""] ?? null;

const actionRouteForSignal = (signal: IExotelContextSignal): RouteKeys | null =>
    ACTION_ROUTE_MAP[signal?.action ?? ""] ?? categoryRouteForSignal(signal);

type TExotelTalkingPointsProps = {
    resolvedCustomerId: string;
    customerIdForProfile: string;
    resolvedPetId: string;
    displayPhone: string;
    resolvedActiveCRT: string;
    agentEmail: string;
    resolvedDidNumber?: string | null;
};

const ExotelTalkingPoints = ({
    resolvedCustomerId,
    customerIdForProfile,
    resolvedPetId,
    displayPhone,
    resolvedActiveCRT,
    agentEmail,
    resolvedDidNumber = null,
}: TExotelTalkingPointsProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getRouteKey } = useRouter();
    const { execute: logClickAPI } = useClickLogAPI();
    const { execute: executeBulkCartUpdate } = useBulkCartUpdateAPI();
    const { execute: fetchContextEngineData, contextEngineData } = useExotelContextEngineAPI();
    const [isTalkingPointsLoading, setIsTalkingPointsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        if (!resolvedCustomerId) return;
        setIsTalkingPointsLoading(true);
        fetchContextEngineData(resolvedCustomerId, resolvedDidNumber).finally(() => setIsTalkingPointsLoading(false));
    }, [resolvedCustomerId, resolvedDidNumber, fetchContextEngineData]);

    useEffect(() => {
        if (!resolvedCustomerId || !contextEngineData) return;
        sessionStorage.setItem(
            "exotel_talking_points_count",
            JSON.stringify({ customerId: resolvedCustomerId, count: contextEngineData.signals?.length ?? 0 }),
        );
        sessionStorage.setItem(
            "exotel_talking_points_data",
            JSON.stringify({ customerId: resolvedCustomerId, data: contextEngineData }),
        );
    }, [resolvedCustomerId, contextEngineData]);

    useEffect(() => {
        setSelectedCategory("All");
    }, [contextEngineData]);

    const signals = contextEngineData?.signals ?? [];
    const categories = ["All", ...new Set(signals.map((item) => item?.category).filter(Boolean))];
    const filteredTalkingPoints =
        selectedCategory === "All"
            ? signals
            : signals.filter((item) => item?.category === selectedCategory);

    const handleSignalNavigate = (routeKey: RouteKeys | null, signal: IExotelContextSignal) => {
        if (!routeKey) return;
        const base = getRouteKey(routeKey, "url");
        if (!base) return;
        if (!CUSTOMER_PROFILE_TAB_ROUTES.has(routeKey)) {
            navigate(base);
            return;
        }
        if (!customerIdForProfile) return;

        // Use pet_id from signal if available
        let petIdToUse = resolvedPetId;
        if (signal?.heading_variables?.pet_id) {
            petIdToUse = signal.heading_variables.pet_id as string;
        }

        const url = petIdToUse
            ? `${base}/${customerIdForProfile}/${petIdToUse}`
            : `${base}/${customerIdForProfile}`;
        navigate(url);
    };

    const navigateForSignal = (signal: IExotelContextSignal, routeKey: RouteKeys | null, isAction = false) => {
        sessionStorage.setItem(
            "exotel_is_upcoming_appointment",
            signal?.is_upcoming_appointment ? "true" : "false",
        );

        sessionStorage.setItem("exotel_selected_signal", JSON.stringify(signal));

        if (routeKey && CUSTOMER_PROFILE_TAB_ROUTES.has(routeKey) && signal?.heading_variables?.pet_id && customerIdForProfile) {
            const signalPetId = signal.heading_variables.pet_id as string;
            dispatch(
                setCustomerAndPetId({
                    customerId: customerIdForProfile,
                    petId: signalPetId,
                }),
            );
        }

        if (signal?.is_book_appointment && isAction) {
            const vars = signal?.heading_variables ?? {};
            const slotsBase = getRouteKey("AVAILABLE_SLOTS", "url");
            if (slotsBase && customerIdForProfile) {
                // A legacy diagnostic appointment is booked as its Diagnostics replacement.
                const signalSubServiceId = ((vars.subservice_id ?? vars.sub_service_id) as string) ?? "";
                const { serviceId, subServiceId } = toDiagnosticsReplacement(
                    (vars.service_id as string) ?? "",
                    signalSubServiceId,
                );
                dispatch(RESET_SLOTS_SLICE());
                dispatch(
                    setSlotBookingFilters({
                        filters: {
                            service_id: serviceId,
                            sub_service_id: subServiceId,
                            service_type_id:
                                subServiceId !== signalSubServiceId
                                    ? DIAGNOSTICS_SERVICE_TYPE_ID
                                    : ((vars.service_type_id as string) ?? ""),
                            clinic_ids: vars.clinic_id ? [(vars.clinic_id as string)] : [],
                            pet_species_id: "",
                            booking_date: format(new Date(), "yyyy-MM-dd"),
                            staff_ids: [],
                            slot_status: "available",
                        },
                    }),
                );
                const url = resolvedPetId
                    ? `${slotsBase}/${customerIdForProfile}/${resolvedPetId}`
                    : `${slotsBase}/${customerIdForProfile}`;
                navigate(url);
                return;
            }
        }
        if (routeKey === "ORDER_MANAGEMENT") {
            const orderSearchPhone = (displayPhone ?? "").replace(/\D/g, "").slice(-10);
            if (orderSearchPhone) {
                const orderBase = getRouteKey("ORDER_MANAGEMENT", "url");
                if (orderBase) {
                    navigate(`${orderBase}?phone_number=${encodeURIComponent(orderSearchPhone)}`);
                    return;
                }
            }
        }
        const redirection = (signal?.redirection ?? "").trim();
        if (redirection && /^https?:/.test(redirection)) {
            window.open(redirection, "_blank");
            return;
        }
        if (redirection && redirection.startsWith("/")) {
            navigate(redirection);
            return;
        }
        handleSignalNavigate(routeKey, signal);
    };

    const handleTalkingPointClick = async (signal: IExotelContextSignal, routeKey: RouteKeys | null, isAction = false) => {
        const logBody: IClickLogBody = {
            event_type: "CONTROL_PANEL_TP_CLICKS",
            scr_id: "TP_CLICK",
            crt_id: resolvedActiveCRT,
            agent_id: agentEmail,
            opened_at: Date?.now(),
            TP_header: signal?.header,
            TP_heading: signal?.heading,
            TP_action_click: isAction ? signal?.action : signal?.redirection,
            TP_summary_list: signal?.heading_variables?.summary_list as string[] | undefined,
        };
        logClickAPI(logBody);

        if (isAction && signal?.action === "Order Again") {
            const orderPayload = signal?.additional_data?.order_payload as IBulkCartUpdatePayload | undefined;
            if (orderPayload?.main_cart_items?.length > 0) {
                const normalizedPayload: IBulkCartUpdatePayload = {
                    ...orderPayload,
                    main_cart_items: orderPayload.main_cart_items.map((item) => ({
                        ...item,
                        unique_row_id: item.unique_row_id ?? crypto.randomUUID(),
                    })),
                };
                await executeBulkCartUpdate(normalizedPayload);
            }
            const hdCartBase = getRouteKey("HD_CART_PAGE", "url");
            if (hdCartBase && customerIdForProfile) {
                navigate(`${hdCartBase}/${customerIdForProfile}`);
            }
            return;
        }

        navigateForSignal(signal, routeKey, isAction);
    };

    if (isTalkingPointsLoading) {
        return (
            <div className="space-y-3 rounded-xl bg-[rgba(249,249,248,1)] mt-2 pt-[12px] pl-[12px] pr-[12px] pb-[12px]">
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em]">Talking Points</div>
                <div className="space-y-[10px]">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-[12px] border border-gray-100 p-[12px] animate-pulse">
                            <div className="h-3 w-1/3 rounded bg-gray-200" />
                            <div className="mt-2 h-3 w-2/3 rounded bg-gray-100" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!signals.length) return null;

    return (
        <div className="space-y-3 rounded-xl bg-[rgba(249,249,248,1)] mt-2 pt-[12px] pl-[12px] pr-[12px] pb-[12px]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em]">Talking Points</div>

            <div className="flex flex-wrap gap-2">
                {categories.map((category) => {
                    const isSelected = selectedCategory === category;
                    const isAll = category === "All";
                    const signal = signals.find((data) => data?.category === category);
                    const style = getThemeStyle(signal?.color);

                    return (
                        <button
                            key={category}
                            type="button"
                            onClick={() => setSelectedCategory(category)}
                            className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-200 ${
                                isSelected && isAll
                                    ? "bg-[#F7F7F6] border-[#6B7280] text-black"
                                    : isSelected
                                        ? `${style?.selectedButton} text-slate-900`
                                        : "border-slate-200 bg-slate-50 text-slate-900 hover:bg-slate-100"
                            }`}
                        >
                            {category}
                        </button>
                    );
                })}
            </div>

            <div className="space-y-[10px]">
                {filteredTalkingPoints.map((signal, index) => {
                    const icon = resolveTalkingPointIcon(signal);
                    const style = getThemeStyle(signal?.color);
                    const categoryRoute = categoryRouteForSignal(signal);
                    const actionRoute = actionRouteForSignal(signal);
                    const viewDetailsRoute = signal?.heading_variables?.isDoctorReminder ? "REMINDERS_TAB" : categoryRoute;

                    return (
                        <div
                            key={signal?.template_id ?? index}
                            className={`rounded-[12px] border p-[12px] ${style?.container}`}
                        >
                            <div
                                className={`flex items-center gap-[6px] text-[11px] font-semibold uppercase tracking-[0.06em] ${style?.label}`}
                            >
                                <span className="text-[13px] leading-none">{icon}</span>
                                <span>{signal?.header}</span>
                            </div>
                            <p
                                className="mt-2 text-xs font-medium"
                                dangerouslySetInnerHTML={{ __html: signal?.heading ?? "" }}
                            />
                            {Array.isArray(signal?.heading_variables?.summary_list) && (
                                <ul className="mt-2 space-y-[2px] pl-3">
                                    {(signal.heading_variables.summary_list as string[]).map((item, i) => (
                                        <li key={i} className="flex items-start gap-1 text-xs font-medium text-gray-700 pt-0.5">
                                            <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-current opacity-50 mt-1.5" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(signal?.action || signal?.redirection) && (
                                <div className="mt-[10px] flex items-center gap-2">
                                    {signal?.action && (
                                        <button
                                            type="button"
                                            onClick={() => handleTalkingPointClick(signal, actionRoute, true)}
                                            className={`rounded-[8px] px-[14px] py-[7px] text-xs font-semibold transition-colors ${style?.solidButton}`}
                                        >
                                            {signal?.action}
                                        </button>
                                    )}
                                    {signal?.redirection && (
                                        <button
                                            type="button"
                                            onClick={() => handleTalkingPointClick(signal, viewDetailsRoute)}
                                            className="rounded-[8px] border border-[rgba(226,226,226,1)] bg-white px-[14px] py-[7px] text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                                        >
                                            View Details
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ExotelTalkingPoints;
